import { useEffect, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface KeyValueEditorProps {
  label: string;
  value: Record<string, string> | undefined;
  onChange: (next: Record<string, string>) => void;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  helpText?: string;
  valueType?: 'text' | 'password';
}

interface Row {
  key: string;
  value: string;
}

function toRows(value: Record<string, string> | undefined): Row[] {
  if (!value) return [];
  return Object.entries(value).map(([key, val]) => ({ key, value: val }));
}

function toRecord(rows: Row[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) continue;
    out[key] = row.value;
  }
  return out;
}

export function KeyValueEditor({
  label,
  value,
  onChange,
  keyPlaceholder = 'Key',
  valuePlaceholder = 'Value',
  helpText,
  valueType = 'text',
}: KeyValueEditorProps) {
  // Rows are local state so that in-progress rows with empty keys
  // (which are valid UI state but filtered out of the emitted record)
  // are not lost on every render.
  const [rows, setRows] = useState<Row[]>(() => toRows(value));

  // Snapshot of the last record we emitted to the parent. Used to detect
  // echoed updates (parent re-renders us with the same value we just sent)
  // and skip re-syncing local state from the prop in that case.
  const lastEmittedRef = useRef<string>(JSON.stringify(toRecord(toRows(value))));

  useEffect(() => {
    const incoming = JSON.stringify(value || {});
    if (incoming === lastEmittedRef.current) return;
    // External update (form reset, edit-mode load, etc.) — re-seed local rows.
    setRows(toRows(value));
    lastEmittedRef.current = incoming;
  }, [value]);

  const emit = (next: Row[]) => {
    setRows(next);
    const record = toRecord(next);
    lastEmittedRef.current = JSON.stringify(record);
    onChange(record);
  };

  const handleKeyChange = (index: number, key: string) => {
    const next = rows.map((row, i) => (i === index ? { ...row, key } : row));
    emit(next);
  };

  const handleValueChange = (index: number, newValue: string) => {
    const next = rows.map((row, i) => (i === index ? { ...row, value: newValue } : row));
    emit(next);
  };

  const handleRemove = (index: number) => {
    const next = rows.filter((_, i) => i !== index);
    emit(next);
  };

  const handleAdd = () => {
    emit([...rows, { key: '', value: '' }]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          className="h-7"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add
        </Button>
      </div>

      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}

      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground italic border border-dashed rounded px-3 py-2">
          No entries. Click Add to create one.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={row.key}
                onChange={(e) => handleKeyChange(index, e.target.value)}
                placeholder={keyPlaceholder}
                className="flex-1 font-mono text-xs"
              />
              <Input
                type={valueType}
                value={row.value}
                onChange={(e) => handleValueChange(index, e.target.value)}
                placeholder={valuePlaceholder}
                className="flex-[2] font-mono text-xs"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemove(index)}
                className="h-9 w-9 p-0"
                aria-label={`Remove ${row.key || 'entry'}`}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
